import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiCheck,
  FiInfo,
  FiChevronUp
} from 'react-icons/fi';
import RubricSelector from './RubricSelector';
import api from '../../services/api';
import logger from '../../utils/logger';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Container = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  padding: 1.5rem;

  @media (max-width: 768px) {
    max-height: 95vh;
    padding: 1.25rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.02em;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: var(--text-primary);
    background: var(--background-secondary);
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  padding: 1.5rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  letter-spacing: -0.01em;
`;

const SelectAllCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(216, 184, 137, 0.05);
    border-color: var(--primary-color);
    box-shadow: var(--shadow-sm);
  }

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--primary-color);
  }

  label {
    cursor: pointer;
    margin: 0;
    flex: 1;
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.875rem;
  }
`;

const SelectionStats = styled.div`
  padding: 0.875rem 1rem;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid var(--success-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;

  strong {
    color: var(--success-color);
    font-weight: 600;
  }
`;


const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid var(--border-color);

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    box-shadow: var(--shadow-sm);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const ErrorAlert = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  align-items: flex-start;

  svg {
    flex-shrink: 0;
    color: var(--error-color);
    margin-top: 0.125rem;
  }

  .message {
    color: var(--error-color);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
  }
`;

const GradeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--background-primary);

  thead {
    background: var(--background-secondary);
    border-bottom: 2px solid var(--border-color);
  }

  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.875rem;
    letter-spacing: -0.01em;
  }

  td {
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-primary);
    font-size: 0.875rem;
  }

  tbody tr {
    transition: background 0.2s ease;

    &:hover {
      background: rgba(216, 184, 137, 0.05);
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--primary-color);
  }

  .grade-cell {
    font-weight: 600;
    font-size: 0.9375rem;
  }

  .letter-grade {
    padding: 0.375rem 0.75rem;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.8125rem;
    display: inline-block;
    min-width: 2rem;
    text-align: center;
    border: 1px solid transparent;

    &.grade-a {
      background: rgba(76, 175, 80, 0.1);
      color: var(--success-dark);
      border-color: var(--success-color);
    }

    &.grade-b {
      background: rgba(33, 150, 243, 0.1);
      color: #2196f3;
      border-color: #2196f3;
    }

    &.grade-c {
      background: rgba(255, 193, 7, 0.1);
      color: #ffc107;
      border-color: #ffc107;
    }

    &.grade-d {
      background: rgba(255, 152, 0, 0.1);
      color: #ff9800;
      border-color: #ff9800;
    }

    &.grade-f {
      background: rgba(244, 67, 54, 0.1);
      color: var(--error-color);
      border-color: var(--error-color);
    }
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary);
  box-shadow: var(--shadow-sm);
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    animation: spin 1s linear infinite;
    color: var(--primary-color);

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  }
`;

const EmptyState = styled.div`
  padding: 2.5rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const DetailsButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-left: 0.5rem;
  vertical-align: middle;

  &:hover {
    background: rgba(216, 184, 137, 0.1);
    color: var(--primary-dark);
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DetailsPanel = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.25rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const CategorySection = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const CategoryName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9375rem;
`;

const CategoryWeight = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CategoryStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.75rem;
  background: var(--background-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  font-size: 0.8125rem;
`;

const ItemName = styled.div`
  color: var(--text-primary);
  flex: 1;
`;

const ItemScore = styled.div`
  color: var(--text-secondary);
  font-weight: 500;
  margin-left: 1rem;
  white-space: nowrap;
`;

const CategoryPercentage = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 0.5rem;
`;

const WeightedContribution = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const TotalBreakdown = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid var(--border-color);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9375rem;
`;

const RubricSelectorContainer = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

/**
 * Bulk Grading Component - NEW PERCENTAGE-WEIGHTED SYSTEM
 * Automatically calculates grades for all students in a course based on selected rubric
 * Displays calculated grades in a table and allows bulk application
 */
function BulkGrading({
  courseId,
  onCancel,
  onComplete
}) {
  const [selectedRubricId, setSelectedRubricId] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [students, setStudents] = useState([]);
  const [calculatedGrades, setCalculatedGrades] = useState({});
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [isCalculating, setIsCalculating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);
  const [appliedResults, setAppliedResults] = useState(null);
  const [hasRubrics, setHasRubrics] = useState(null);
  const [expandedStudentId, setExpandedStudentId] = useState(null);

  // Fetch enrolled students when component mounts
  useEffect(() => {
    if (!courseId) return;

    const fetchStudents = async () => {
      try {
        const response = await api.request(`/courses/${courseId}/students`);
        if (response.success && response.data) {
          setStudents(response.data.filter(s => s.role === 'student'));
        }
      } catch (err) {
        logger.error('Error fetching course students:', err);
        setError('Failed to load students');
      }
    };

    fetchStudents();
  }, [courseId]);

  const handleRubricSelect = async (rubricId, rubricData) => {
    setSelectedRubricId(rubricId);
    setRubric(rubricData);
    setCalculatedGrades({});
    setSelectedStudentIds(new Set());
    setError(null);

    // Calculate grades for all students
    await calculateAllGrades(rubricId);
  };

  const handleRubricsLoaded = (rubricsList) => {
    if (!Array.isArray(rubricsList)) {
      setHasRubrics(null);
    } else {
      setHasRubrics(rubricsList.length > 0);
    }
  };

  const calculateAllGrades = async (rubricId) => {
    setIsCalculating(true);
    const grades = {};

    try {
      for (const student of students) {
        try {
          const response = await api.request(
            `/final-grades/students/${student.id}/courses/${courseId}?rubricId=${rubricId}`
          );

          if (response.success && response.data) {
            grades[student.id] = response.data;
          } else {
            grades[student.id] = { error: response.message || 'Calculation failed' };
          }
        } catch (err) {
          logger.error(`Error calculating grade for student ${student.id}:`, err);
          grades[student.id] = { error: err.message || 'Error calculating grade' };
        }
      }

      setCalculatedGrades(grades);
      // Auto-select all students that have valid grades
      const validStudents = Object.keys(grades).filter(
        sid => grades[sid].finalPercentage !== undefined
      );
      setSelectedStudentIds(new Set(validStudents.map(Number)));
    } catch (err) {
      logger.error('Error calculating all grades:', err);
      setError('Failed to calculate grades');
    } finally {
      setIsCalculating(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    const newSet = new Set(selectedStudentIds);
    if (newSet.has(studentId)) {
      newSet.delete(studentId);
    } else {
      newSet.add(studentId);
    }
    setSelectedStudentIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedStudentIds.size === students.length) {
      setSelectedStudentIds(new Set());
    } else {
      setSelectedStudentIds(new Set(students.map(s => s.id)));
    }
  };

  const handleApplyGrades = async () => {
    if (!selectedRubricId) {
      setError('Please select a rubric');
      return;
    }

    if (selectedStudentIds.size === 0) {
      setError('Please select at least one student');
      return;
    }

    setIsApplying(true);
    const results = { successful: 0, failed: 0, errors: [] };

    try {
      for (const studentId of Array.from(selectedStudentIds)) {
        try {
          // Get the calculated grade for this student
          const gradeData = calculatedGrades[studentId];
          
          if (!gradeData || gradeData.error) {
            results.failed++;
            results.errors.push({
              studentId,
              error: gradeData?.error || 'Grade calculation failed. Please recalculate grades.'
            });
            continue;
          }

          // Missing grades are automatically treated as zero (default behavior)

          // Use the same endpoint and method as individual "Assign Course Grade" modal
          // PUT /api/courses/:courseId/students/:studentId/grade
          const response = await api.assignCourseGrade(
            courseId,
            studentId,
            gradeData.letterGrade,
            null, // notes
            Math.round(gradeData.finalPercentage) // numericGrade
          );

          if (response && response.success) {
            results.successful++;
          } else {
            results.failed++;
            const errorMessage = response?.message || response?.error || 'Unknown error occurred';
            results.errors.push({
              studentId,
              error: errorMessage
            });
          }
        } catch (err) {
          results.failed++;
          // Extract error message from API error response
          let errorMessage = 'Unknown error occurred';
          if (err.data) {
            errorMessage = err.data.message || err.data.error || err.message;
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          results.errors.push({
            studentId,
            error: errorMessage
          });
          
          logger.error('Error assigning grade to student', {
            studentId,
            courseId,
            error: errorMessage,
            fullError: err
          });
        }
      }

      setAppliedResults(results);
    } catch (err) {
      logger.error('Error applying grades:', err);
      const errorMessage = err.data?.message || err.message || 'Failed to apply grades';
      setError(errorMessage);
    } finally {
      setIsApplying(false);
    }
  };

  // Show results screen
  if (appliedResults) {
    return (
      <Overlay onClick={onCancel}>
        <Container onClick={e => e.stopPropagation()}>
          <Header>
            <h2>Grades Applied</h2>
            {onCancel && (
              <CloseButton onClick={onCancel}>
                <FiX size={24} />
              </CloseButton>
            )}
          </Header>

          <Section style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2.5rem',
                marginBottom: '2rem'
              }}
            >
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--success-color)', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
                  {appliedResults.successful}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Applied Successfully
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: appliedResults.failed > 0 ? 'var(--error-color)' : 'var(--text-secondary)',
                    marginBottom: '0.5rem',
                    letterSpacing: '-0.02em'
                  }}
                >
                  {appliedResults.failed}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Failed
                </div>
              </div>
            </div>

            {appliedResults.errors.length > 0 && (
              <div style={{ marginTop: '1.5rem', textAlign: 'left', padding: '1rem', background: 'rgba(244, 67, 54, 0.05)', borderRadius: '12px', border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                <h4 style={{ color: 'var(--error-color)', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>Errors:</h4>
                {appliedResults.errors.map((err, idx) => {
                  const student = students.find(s => s.id === err.studentId);
                  const studentName = student 
                    ? `${student.first_name} ${student.last_name}` 
                    : `Student ${err.studentId}`;
                  return (
                    <div key={idx} style={{ fontSize: '0.8125rem', color: 'var(--error-color)', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                      <strong>{studentName}</strong>: {err.error}
                    </div>
                  );
                })}
              </div>
            )}

            <ActionButtons style={{ justifyContent: 'center', marginTop: '2rem' }}>
              <Button
                className="primary"
                onClick={() => {
                  if (onComplete) {
                    onComplete(appliedResults);
                  }
                  if (onCancel) {
                    onCancel();
                  }
                }}
              >
                <FiCheckCircle size={16} />
                Done
              </Button>
            </ActionButtons>
          </Section>
        </Container>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onCancel}>
      <Container onClick={e => e.stopPropagation()}>
        <Header>
          <h2>Bulk Grade Calculator</h2>
          {onCancel && (
            <CloseButton onClick={onCancel}>
              <FiX size={24} />
            </CloseButton>
          )}
        </Header>

        {error && (
          <ErrorAlert>
            <FiAlertCircle size={20} />
            <div className="message">{error}</div>
          </ErrorAlert>
        )}

        <Form onSubmit={(e) => { e.preventDefault(); handleApplyGrades(); }}>
          {/* Rubric Selection */}
          <RubricSelectorContainer>
            <SectionTitle>Select Rubric to Calculate Grades</SectionTitle>
            <RubricSelector
              courseId={courseId}
              onSelect={handleRubricSelect}
              selectedRubricId={selectedRubricId}
            onRubricsLoaded={handleRubricsLoaded}
            />
          </RubricSelectorContainer>
        {hasRubrics === false && !error && !isCalculating && (
          <EmptyState>
            <p>No rubrics exist for this course yet.</p>
            <p style={{ marginTop: '0.5rem' }}>
              Create a rubric in the Create Rubric tab to begin bulk grading.
            </p>
          </EmptyState>
        )}

          {/* Grades Table */}
          {rubric && students.length > 0 && (
            <>
              <Section>
                <div style={{ marginBottom: '1rem' }}>
                  <SelectAllCheckbox>
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.size === students.length && students.length > 0}
                      onChange={toggleSelectAll}
                      id="select-all-students"
                    />
                    <label htmlFor="select-all-students">
                      Select All ({selectedStudentIds.size} of {students.length})
                    </label>
                  </SelectAllCheckbox>
                </div>

                {isCalculating ? (
                  <LoadingContainer>
                    <FiLoader size={24} />
                    <span>Calculating grades for all students...</span>
                  </LoadingContainer>
                ) : (
                  <TableContainer>
                    <GradeTable>
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={selectedStudentIds.size === students.length && students.length > 0}
                              onChange={toggleSelectAll}
                            />
                          </th>
                          <th>Name</th>
                          <th>Email</th>
                          <th style={{ width: '120px' }}>Percentage</th>
                          <th style={{ width: '80px' }}>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map(student => {
                          const gradeData = calculatedGrades[student.id];
                          const isChecked = selectedStudentIds.has(student.id);

                          return (
                            <React.Fragment key={student.id}>
                              <tr>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleStudentSelection(student.id)}
                                    disabled={!gradeData || gradeData.error}
                                  />
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span>{student.first_name} {student.last_name}</span>
                                    {gradeData?.categoryBreakdown && (
                                      <DetailsButton
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setExpandedStudentId(
                                            expandedStudentId === student.id ? null : student.id
                                          );
                                        }}
                                        title="Show grade breakdown"
                                      >
                                        {expandedStudentId === student.id ? (
                                          <FiChevronUp />
                                        ) : (
                                          <FiInfo />
                                        )}
                                      </DetailsButton>
                                    )}
                                  </div>
                                </td>
                                <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                  {student.email}
                                </td>
                                <td className="grade-cell">
                                  {gradeData?.error ? (
                                    <span style={{ color: 'var(--error-color)' }}>
                                      Error: {gradeData.error}
                                    </span>
                                  ) : gradeData?.finalPercentage !== undefined ? (
                                    `${gradeData.finalPercentage}%`
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)' }}>--</span>
                                  )}
                                </td>
                                <td>
                                  {gradeData?.letterGrade ? (
                                    <span className={`letter-grade grade-${gradeData.letterGrade.toLowerCase()}`}>
                                      {gradeData.letterGrade}
                                    </span>
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)' }}>--</span>
                                  )}
                                </td>
                              </tr>
                              {expandedStudentId === student.id && gradeData?.categoryBreakdown && (
                                <tr>
                                  <td colSpan={5} style={{ padding: 0, borderBottom: 'none' }}>
                                    <DetailsPanel>
                                      <div style={{ marginBottom: '1rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
                                        Grade Breakdown for {student.first_name} {student.last_name}
                                      </div>
                                      {gradeData.categoryBreakdown.map((category) => (
                                        <CategorySection key={category.categoryId}>
                                          <CategoryHeader>
                                            <CategoryName>{category.categoryName}</CategoryName>
                                            <CategoryWeight>{category.weight}% weight</CategoryWeight>
                                          </CategoryHeader>
                                          <ItemList>
                                            {category.items && category.items.length > 0 ? (
                                              category.items.map((item, idx) => (
                                                <ItemRow key={idx}>
                                                  <ItemName>
                                                    {item.name} ({item.type})
                                                  </ItemName>
                                                  <ItemScore>
                                                    {item.score} / {item.maxScore} 
                                                    {item.percentage !== undefined && ` (${item.percentage.toFixed(1)}%)`}
                                                  </ItemScore>
                                                </ItemRow>
                                              ))
                                            ) : (
                                              <ItemRow>
                                                <ItemName style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                                  No items assigned
                                                </ItemName>
                                              </ItemRow>
                                            )}
                                          </ItemList>
                                          <CategoryStats>
                                            <CategoryPercentage>
                                              Category Score: {category.categoryScore} / {category.categoryMaxScore} 
                                              ({category.categoryPercentage.toFixed(2)}%)
                                            </CategoryPercentage>
                                          </CategoryStats>
                                          <WeightedContribution>
                                            Weighted Contribution: {category.weightedContribution.toFixed(2)}%
                                          </WeightedContribution>
                                        </CategorySection>
                                      ))}
                                      <TotalBreakdown>
                                        Final Grade: {gradeData.finalPercentage.toFixed(2)}% ({gradeData.letterGrade})
                                      </TotalBreakdown>
                                    </DetailsPanel>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </GradeTable>
                  </TableContainer>
                )}

                {selectedStudentIds.size > 0 && !isCalculating && (
                  <SelectionStats style={{ marginTop: '1rem' }}>
                    Ready to apply grades to <strong>{selectedStudentIds.size}</strong> student
                    {selectedStudentIds.size !== 1 ? 's' : ''}
                  </SelectionStats>
                )}
              </Section>

              <ActionButtons>
                {onCancel && (
                  <Button
                    type="button"
                    className="secondary"
                    onClick={onCancel}
                    disabled={isApplying}
                  >
                    <FiX size={16} />
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  className="primary"
                  disabled={isApplying || isCalculating || selectedStudentIds.size === 0}
                >
                  <FiCheck size={16} />
                  {isApplying
                    ? `Applying to ${selectedStudentIds.size} student${selectedStudentIds.size !== 1 ? 's' : ''}...`
                    : `Apply Grades to ${selectedStudentIds.size} Student${selectedStudentIds.size !== 1 ? 's' : ''}`}
                </Button>
              </ActionButtons>
            </>
          )}

          {rubric && students.length === 0 && !isCalculating && (
            <EmptyState>
              <p>No students enrolled in this course</p>
            </EmptyState>
          )}
        </Form>
      </Container>
    </Overlay>
  );
}

export default BulkGrading;
